import { idb as createDatabase } from '@codesuma/baseline'
import type { ICollection } from './index'
import type { CollectionName } from './rows'
import { COLLECTIONS } from './rows'

const db = createDatabase('emrouz')

// The same names the SQLite schema uses, hyphens kept: IndexedDB store names
// may contain them, and they already match the localStorage keys. A name added
// here reaches an existing database through ensureStores below.
const STORES = ['tasks', 'events', 'custom-occasions', 'notes', 'occasion-overrides', 'day-colors', 'meta'] as const

// createStore opens at an explicit version; opening below the current one
// throws VersionError. Read first, then only bump upward for a missing store.
export async function ensureStores() {
    for (const name of STORES) {
        const { version, stores } = await db.info()
        if (stores.includes(name)) continue
        await db.createStore(name, version + 1)
    }
}

export const idb = <T extends { id: string }>(name: string): ICollection<T> => ({
    async load() {
        // all(), not find() — find's default limit is 1000.
        const rows = await db.all<T>(name)
        return rows.filter(row => row && typeof row.id === 'string')
    },

    async put(row) {
        await db.save(name, row)
    },

    async remove(id) {
        await db.delete(name, id)
    },
})

// The collections first, one save each so a collection lands whole, then the
// flag. Baseline's idb cannot span stores in one transaction, so a tear here
// leaves the flag unset: the next launch retries and the count check skips
// every store that already holds rows. The localStorage copy is left alone.
export async function importIntoIdb(prepared: Record<CollectionName, { id: string }[]>) {
    const flag = await db.get<{ id: string; value?: string }>('meta', 'imported')
    if (flag?.value === '1') return
    for (const name of COLLECTIONS) {
        if (await db.count(name) > 0) continue
        const rows = prepared[name]
        if (rows.length) await db.save(name, rows)
    }
    await db.save('meta', { id: 'imported', value: '1' })
}
